import React from 'react'
import moment from 'moment'

import BeachAccessIcon from '@mui/icons-material/BeachAccess'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import SickIcon from '@mui/icons-material/Sick'

import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { getAvailabilityBandStyle } from './globalRosterUtils'

export const hatchPattern = (colour) =>
  `repeating-linear-gradient(135deg, transparent 0 6px, ${colour} 6px 8px)`

const getIcon = (record) => {
  if (record.type === 'Available') return EventAvailableIcon
  if (record.status === 'Pending') return HourglassEmptyIcon
  if (record.type === 'Annual Leave') return BeachAccessIcon
  if (record.type === 'Sickness') return SickIcon
  return EventBusyIcon
}

// A staff availability record drawn as a slim band spanning its days on the
// week grid (see layoutRowAvailability). Sits above the row's shift cards.
const RosterAvailabilityBand = ({
  record,
  continuesFromPrior = false,
  continuesToNext = false,
  visibleSpan = 1,
  onClick,
}) => {
  const style = getAvailabilityBandStyle(record)
  const Icon = getIcon(record)
  const isAvailable = record.type === 'Available'
  const isPending = record.status === 'Pending'
  const isMultiDay = !moment(record.start).isSame(moment(record.end), 'day')
  const timeFormat = isMultiDay && visibleSpan > 1 ? 'ddd HH:mm' : 'HH:mm'
  const title = isAvailable
    ? 'Available'
    : isPending
      ? `${record.type} (pending)`
      : record.type
  const timeLabel = record.allDay
    ? isMultiDay && visibleSpan > 1
      ? `${moment(record.start).format('ddd D')} – ${moment(record.end).format('ddd D')}`
      : 'All day'
    : `${moment(record.start).format(timeFormat)}–${moment(record.end).format(timeFormat)}`
  // A single day column has room for one thing: the time for an available
  // window (the green band says "available"), the reason for leave. The
  // tooltip always carries the full detail.
  const compact = visibleSpan === 1
  const shortType = { 'Annual Leave': 'Leave', Sickness: 'Sick' }[record.type] ?? record.type
  const label = compact
    ? isAvailable
      ? timeLabel
      : `${shortType}${isPending ? ' · pending' : ''}`
    : `${title} · ${timeLabel}`
  const fullRange = `${moment(record.start).format('ddd D MMM HH:mm')} – ${moment(
    record.end,
  ).format('ddd D MMM HH:mm')}`
  const tooltip = [`${title}: ${fullRange}`, record.reason]
    .filter(Boolean)
    .join(' — ')

  return (
    <Tooltip title={tooltip}>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          minWidth: 0,
          borderRadius: 1,
          bgcolor: style.bg,
          color: style.fg,
          border: '1px solid',
          borderColor: style.border,
          backgroundImage: style.hatched ? hatchPattern(`${style.border}66`) : 'none',
          cursor: onClick ? 'pointer' : 'default',
          '& svg': { fontSize: 14, flexShrink: 0 },
        }}
      >
        <Icon />
        <Typography variant="caption" fontWeight={700} color="inherit" noWrap>
          {continuesFromPrior ? '← ' : ''}
          {label}
          {continuesToNext ? ' →' : ''}
        </Typography>
      </Box>
    </Tooltip>
  )
}

export default RosterAvailabilityBand
